import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SingleCustomCollectionTable from './components/SingleCustomCollectionTable'
import { useItemSelection } from './hooks/useItemSelection'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import useSingleDependentCustomCollection from './useSingleCustomDependentCollection'
import { useNotifications } from '@/hooks/useNotifications'
import { useNavigate } from 'react-router-dom'

export default function DependentCollectionTableCard({ collectionId, parentItem, filterFieldName, filterFieldValue, addNewReturnPath }) {
    const { t } = useTranslation()
    // console.log('collectionId:', collectionId);
    // console.log('parentItem:', parentItem);
    // console.log('filterFieldName:', filterFieldName);
    // console.log('filterFieldValue:', filterFieldValue);
    // console.log('addNewReturnPath:', addNewReturnPath);

    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate()

    // Item selection hook
    const { selectedItems, handleSelectItem, handleSelectAll, clearSelection, getSelectionState } = useItemSelection()

    const {
        items,
        collectionName,
        collectionDetails,
        loading,
        loadingCollection,
        loadingMore,
        deleting,
        error,
        message,
        totalCount,
        hasMore,
        filters,
        sortColumn,
        sortDirection,
        initializeCollection,
        loadItems,
        handleLoadMore,
        deleteItem,
        setSorting,
        updateFilters,
        setFilters,
        clearError,
        clearMessage,
    } = useSingleDependentCustomCollection(collectionId, filterFieldName, filterFieldValue)

    // Initialize the collection when collectionId is available
    useEffect(() => { if (collectionId) { initializeCollection() } }, [collectionId, initializeCollection])

    // Load items after collection is initialized
    useEffect(() => { if (collectionName) { loadItems() } }, [collectionName, loadItems])

    // Notifications hook
    const { showDeleteConfirmation, showDeleteSuccess } = useNotifications({ error, message, clearError, clearMessage })

    // Handle search with debouncing
    useEffect(() => {
        // console.log("TCL -> CustomCollections -> updateFilters:", updateFilters)
        const timer = setTimeout(() => {
            const v = (searchTerm || '').trim()
            if (v.length === 0 || v.length >= 3) {
                updateFilters({ searchText: v })
            }
        }, 200)

        return () => clearTimeout(timer)
    }, [searchTerm, updateFilters])

    // Handle search
    const handleSearchChange = e => {
        setSearchTerm(e?.target ? e.target.value : e)
    }

    // Handle column filter updates
    const handleColumnFilter = (columnName, filterValue) => {
        // Get current filters and create new object
        const currentFilters = { ...filters }

        if (filterValue === null) {
            // Remove filter completely
            delete currentFilters[columnName]
        } else {
            // Add/update filter
            currentFilters[columnName] = filterValue
        }

        // Replace all filters using setFilters
        setFilters(currentFilters)
        // console.log("TCL -> handleColumnFilter -> currentFilters:", currentFilters)
    }

    // Handle delete selected
    const handleDeleteSelected = async () => {
        if (selectedItems.size === 0) return

        const confirmed = await showDeleteConfirmation(
            selectedItems.size,
            t('singleCustomCollection.list.deleteDialog.confirmText', { count: selectedItems.size })
        )

        if (confirmed) {
            const deletePromises = Array.from(selectedItems).map(itemId => deleteItem(itemId))

            try {
                await Promise.all(deletePromises)
                clearSelection()
            } catch (error) {
                console.error('Error deleting items:', error)
            }
        }
    }

    // Handle column sorting
    const handleSort = column => {
        const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
        setSorting(column, newDirection)
    }

    // Handle navigation to add page
    const handleAddNew = () => {
        const collectionLinkField = collectionDetails?.fields.find(f => f.name === filterFieldName)
        // console.log(`TCL -> DependentCollectionTableCard.jsx:160 -> DependentCollectionTableCard -> collectionLinkField:`, collectionLinkField)
        const collectionLinkFieldLabel = collectionLinkField ? parentItem?.[collectionLinkField.lookupDisplayField] || parentItem?.[collectionLinkField.name] || filterFieldValue : filterFieldValue
        // console.log(`TCL -> DependentCollectionTableCard.jsx:162 -> DependentCollectionTableCard -> collectionLinkFieldLabel:`, collectionLinkFieldLabel)

        navigate(`/single-custom-collection/${collectionId}/add?returnPath=${encodeURIComponent(addNewReturnPath)}&presetFieldName=${encodeURIComponent(filterFieldName)}&presetFieldLabel=${encodeURIComponent(collectionLinkFieldLabel)}&presetFieldValue=${encodeURIComponent(filterFieldValue)}`)
    }

    // Wrapper for select all to match component interface
    const onSelectAll = checked => {
        handleSelectAll(checked, items)
    }

    // Get selection state
    const { isAllSelected, isIndeterminate } = getSelectionState(items)

    // Show loading indicator while loading collection details
    if (loadingCollection) {
        return <LoadingIndicator message={t('singleCustomCollection.list.loading')} />
    }

    // Show error if collection not found
    if (!collectionName && !loadingCollection) {
        return (
            <div className="text-center py-12 px-4 text-gray-500 bg-white rounded-lg">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('singleCustomCollection.list.notFound.title')}</h2>
                <p className="text-gray-600">{t('singleCustomCollection.list.notFound.description')}</p>
                <button
                    onClick={() => navigate('/system/custom-collections')}
                    className="mt-4 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                    {t('singleCustomCollection.list.notFound.backButton')}
                </button>
            </div>
        )
    }

    const buttons = [
        { type: 'delete', label: t('singleCustomCollection.list.actions.delete'), count: selectedItems.size, onClick: handleDeleteSelected, loading: deleting },
        { type: 'add', label: t('singleCustomCollection.list.actions.addNew'), onClick: handleAddNew },
    ]

    return (
        <div className="rounded-lg border border-gray-100 bg-white shadow-sm mt-6">
            <div className="flex flex-col gap-1 px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">{collectionName}</h3>
                    </div>
                    <div className="text-sm text-gray-500">
                        <span>{t('singleCustomCollection.list.summary.totalItems', { count: totalCount })}</span>
                    </div>
                </div>
            </div>

            <div className="px-4 py-3">
                <SearchActionsBar
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder={t('singleCustomCollection.list.searchPlaceholder')}
                    buttons={buttons}
                />

                <SingleCustomCollectionTable
                    items={items}
                    collectionDetails={collectionDetails}
                    loading={loading}
                    selectedItems={selectedItems}
                    onSelectAll={onSelectAll}
                    onSelectItem={handleSelectItem}
                    isAllSelected={isAllSelected}
                    isIndeterminate={isIndeterminate}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    collectionId={collectionId}
                    filters={filters}
                    onApplyFilter={handleColumnFilter}

                />

                <LoadMoreSection
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    onLoadMore={handleLoadMore}
                    dataLength={items.length}
                    totalCount={totalCount}
                />
            </div>

        </div>
    )
}
